import React, { useState } from 'react';
import { TylerState, updateReact, useTylerStateUpdate } from '../../utils/TylerState';
import { ForecastAccount, CURRENCY_SYMBOLS } from '../../types/personalForecast';
import { addMonths, currentMonth, monthsBetween, getFXRate, latestBalance } from '../../utils/forecastEngine';

/**
 * Month-by-month balances the user actually had, one row per account, from a chosen start
 * month up to today. The forecast begins from the latest month filled in. Mirrors the
 * "Actual" columns of a spreadsheet model.
 */
interface Props {
  onPaste: () => void;
}

const BalanceHistory: React.FC<Props> = ({ onPaste }) => {
  useTylerStateUpdate();
  const { accounts, settings, fxRates } = TylerState.forecast;
  const today = currentMonth();
  const [pendingStart, setPendingStart] = useState(addMonths(today, -6));

  const start = settings.historyStartMonth;
  const months = start && start <= today
    ? Array.from({ length: monthsBetween(start, today) }, (_, i) => addMonths(start, i))
    : [];

  const markDirty = () => {
    TylerState.forecast.isDirty = true;
    updateReact();
  };

  const setStart = (month: string) => {
    settings.historyStartMonth = month;
    markDirty();
  };

  const setActual = (acct: ForecastAccount, month: string, raw: string) => {
    const actuals = { ...(acct.actuals || {}) };
    if (raw.trim() === '') {
      delete actuals[month];
    } else {
      const value = parseFloat(raw);
      if (Number.isNaN(value)) return;
      actuals[month] = value;
    }
    acct.actuals = actuals;
    markDirty();
  };

  /** The balance in force for a month: the latest recorded value on or before it, else the opening balance. */
  const balanceAt = (acct: ForecastAccount, month: string): number => {
    const recorded = Object.keys(acct.actuals || {}).filter(m => m <= month).sort();
    return recorded.length ? acct.actuals![recorded[recorded.length - 1]] : acct.openingBalance;
  };

  const netWorthAt = (month: string): number =>
    accounts.reduce((sum, acct) => {
      const rate = getFXRate(acct.currency, settings.baseCurrency, fxRates);
      const balance = balanceAt(acct, month) * rate;
      return sum + (acct.side === 'asset' ? balance : -Math.abs(balance));
    }, 0);

  const monthLabel = (month: string) =>
    new Date(month + '-01T00:00:00').toLocaleDateString('en-CA', { month: 'short', year: '2-digit' });

  const money = (amount: number, currency = settings.baseCurrency) => {
    const abs = Math.abs(amount).toLocaleString('en-CA', { maximumFractionDigits: 0 });
    return `${amount < 0 ? '-' : ''}${CURRENCY_SYMBOLS[currency]}${abs}`;
  };

  return (
    <div className="account-section" style={{ marginTop: '2.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Balance History</h3>
        {start ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button className="btn-secondary" onClick={onPaste} style={{ padding: '6px 14px', fontSize: '0.9rem' }}>
              Paste from spreadsheet
            </button>
            <label style={{ fontSize: '0.9rem', color: '#64748b' }}>
              History starts{' '}
              <input type="month" value={start} max={today} onChange={e => setStart(e.target.value)} style={{ marginLeft: '6px', padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px' }} />
            </label>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }}>
            <input type="month" value={pendingStart} max={today} onChange={e => setPendingStart(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px' }} />
            <button className="btn-secondary" onClick={() => setStart(pendingStart)} style={{ padding: '6px 14px', fontSize: '0.9rem' }}>
              Start tracking history
            </button>
          </div>
        )}
      </div>
      <p className="help-text" style={{ marginTop: 0 }}>
        {accounts.length === 0
          ? 'Add your accounts above, pick the month your records start, then type each month-end balance here. The forecast picks up from the latest month you fill in.'
          : "Type each account's month-end balance. The forecast starts from the latest month you fill in; blank months carry the previous balance forward."}
      </p>

      {months.length > 0 && accounts.length === 0 && (
        <p style={{ color: '#94a3b8', fontStyle: 'italic', padding: '0.5rem 0' }}>
          History runs from {monthLabel(months[0])} to {monthLabel(months[months.length - 1])}. Accounts you add will appear here as rows.
        </p>
      )}

      {months.length > 0 && accounts.length > 0 && (
        <div className="frozen-table-wrap">
          <table className="accounts-table history-grid frozen-table">
            <thead>
              <tr>
                <th>Account</th>
                {months.map(m => <th key={m} style={{ textAlign: 'right' }}>{monthLabel(m)}</th>)}
                <th style={{ textAlign: 'right' }}>Latest</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(acct => (
                <tr key={acct.id}>
                  <td>
                    {acct.name || '(unnamed)'}
                    <span style={{ color: '#94a3b8', marginLeft: '6px', fontSize: '0.8rem' }}>{acct.currency}</span>
                  </td>
                  {months.map(m => {
                    const recorded = acct.actuals?.[m];
                    return (
                      <td key={m}>
                        <input
                          type="number"
                          value={recorded ?? ''}
                          placeholder="0"
                          onChange={e => setActual(acct, m, e.target.value)}
                          aria-label={`${acct.name || 'account'} balance ${monthLabel(m)}`}
                          style={{ width: '96px', padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: '4px', textAlign: 'right' }}
                        />
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'right', fontWeight: 600, color: acct.side === 'asset' ? '#059669' : '#dc2626' }}>
                    {money(latestBalance(acct), acct.currency)}
                  </td>
                </tr>
              ))}
              <tr className="total-row">
                <td><strong>Net worth ({settings.baseCurrency})</strong></td>
                {months.map(m => (
                  <td key={m} style={{ textAlign: 'right', fontWeight: 700 }}>{money(netWorthAt(m))}</td>
                ))}
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(netWorthAt(today))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BalanceHistory;
